import { openaiCompatFetch } from '@/utils/openaiProxyFetch';
import { getOpenAIKey, getOpenAIModel } from '@/services/llmShared';
import { PromptParts } from "./types";

// Character generation block for human subjects — ensures diversity
const CHARACTER_GENERATION_BLOCK = `[CHARACTER GENERATION — DIVERSITY LOCK]
Generate ONE completely unique human subject. Hard requirements:
- DO NOT reuse any previously generated face or default to familiar archetypes
- Randomize: Age (25-75), Ethnicity (bias toward uncommon combinations), Facial Structure, Hair, Body Type, Skin Condition
- Expression: choose ONE dominant emotion (rage, grief, paranoia, exhaustion, defiance, intensity, focus)
- MANDATORY minimum 2 distinct traits: visible scars, crooked nose, faded tattoos, weathered hands, deep wrinkles, chipped teeth
- Subject must look TOUGH and LIVED-IN — "been through hell" not impaired
- If subject resembles a common cinematic archetype, regenerate internally before rendering
Character ID: ${Math.random().toString(36).substring(2, 8)}-${Date.now().toString(36)}`;

// Detect if subject likely contains a human
function subjectContainsHuman(subject: string): boolean {
  const humanTerms = /\b(man|woman|person|figure|character|singer|musician|guitarist|drummer|vocalist|worker|laborer|cowboy|outlaw|preacher|mechanic|bartender|trucker|biker|farmer|soldier|drifter|stranger|kid|elder|old man|old woman|young man|young woman|he|she|they|him|her|his|portrait|face|human|people|crowd|band|group)\b/i;
  return humanTerms.test(subject);
}

// Get aspect ratio label for prompt
function getAspectLabel(ratio: string): string {
  const labels: Record<string, string> = {
    '1:1': '1:1 square album cover',
    '5:7': '5:7 vertical album cover',
    '3:2': '3:2 landscape',
    '16:9': '16:9 widescreen',
    '4:5': '4:5 portrait',
    '3:4': '3:4 tall portrait',
    '2:3': '2:3 tall vertical',
    '3:1': '3:1 ultra-wide banner',
  };
  return labels[ratio] || ratio;
}

export const buildPromptString = (parts: PromptParts): string => {
  const lines: string[] = [];
  const hasAlbumText = !!(parts.songTitle || parts.artistName);
  const aspect = parts.aspectRatio || '1:1';

  // 1. Aspect ratio + subject + action + environment as flowing prose (aspect ratio FIRST per Images 2.0)
  let opening = `${getAspectLabel(aspect)}.`;
  if (parts.subject) opening += ` ${parts.subject}`;
  if (parts.action) opening += `, ${parts.action}`;
  if (parts.environment) opening += `. ${parts.environment}`;
  if (parts.mood) opening += `. ${parts.mood}`;
  lines.push(opening.replace(/\.\./g, '.').trim());

  // 2. Album text elements — quoted for literal rendering
  if (hasAlbumText) {
    const textMedium = parts.textMedium || 'stylized display typography';
    if (parts.songTitle) {
      lines.push(`"${parts.songTitle.toUpperCase()}" — rendered as ${textMedium}, placed in the top 18-22% safe zone with at least 5% edge padding. Fully visible, never clipped.`);
    }
    if (parts.artistName) {
      lines.push(`"${parts.artistName}" — smaller elegant secondary typography in the bottom 12-15% safe zone, strong readability.`);
    }
    // Composition lock for album covers
    lines.push('Compose as a professional album cover with intentional negative space at top and bottom. Subject framed slightly below center. Typography readability takes priority over cinematic framing. Text zones must have controlled contrast for legibility.');
    // Anti-lyric guardrail — only the title and artist name should appear as text in the scene.
    // Bypasses the negatives-strip filter below, which would otherwise remove any "NO text" clause
    // because GPT-Image-2 takes it too literally.
    lines.push('Song lyrics, verses, or lyrical fragments must NOT appear as written text, graffiti, banners, or signage within the scene. Only the song title and artist name are rendered as visible text — no other lyric content.');
  }

  // 3. Style anchor — concrete, editorial
  if (parts.style) {
    lines.push(parts.style);
  }

  // 4. Lighting and color — separate instruction
  if (parts.lighting) {
    lines.push(parts.lighting);
  }

  // 5. Camera and composition
  if (parts.camera) {
    lines.push(parts.camera);
  }

  // 6. Detail and texture + quality merged
  const materialParts = [parts.detail, parts.quality].filter(Boolean);
  if (materialParts.length > 0) {
    lines.push(materialParts.join('. '));
  }

  // 7. Character diversity block for human subjects
  if (parts.subject && subjectContainsHuman(parts.subject)) {
    lines.push(CHARACTER_GENERATION_BLOCK);
  }

  // 8. Negatives — NO X format
  if (parts.negatives) {
    // When album text (title/artist) should render, strip text-related negatives —
    // GPT-Image-2 takes "NO TEXT" literally and refuses to render the title.
    const textNegativePattern = /\b(text|typography|lettering|letters|words|writing|caption|title|font|fonts|type)\b/i;
    const normalized = parts.negatives
      .split(/,\s*/)
      .map(n => {
        const trimmed = n.trim();
        if (!trimmed) return '';
        if (hasAlbumText && textNegativePattern.test(trimmed)) return '';
        // Already starts with NO
        if (/^NO\s/i.test(trimmed)) return trimmed;
        // Starts with "no " lowercase
        if (/^no\s/i.test(trimmed)) return 'NO ' + trimmed.replace(/^no\s+/i, '');
        return 'NO ' + trimmed;
      })
      .filter(Boolean)
      .join(', ');
    if (normalized) lines.push(normalized);
  }

  return lines.filter(l => l.trim().length > 0).join('\n');
};

interface OpenAIChatResponse {
  choices?: { message?: { content?: string } }[];
  error?: { message?: string };
}

export const parsePrompt = async (userInput: string, previousSubjects?: string[]): Promise<PromptParts> => {
  const apiKey = getOpenAIKey();
  if (!apiKey) {
    throw new Error('OpenAI API key not found. Add your key in Settings.');
  }
  const model = getOpenAIModel();

  // Build anti-clone context
  let antiCloneInstruction = '';
  if (previousSubjects && previousSubjects.length > 0) {
    antiCloneInstruction = `\n\nANTI-CLONE SYSTEM: The following subjects have been used in recent art prompts. Generate a COMPLETELY DIFFERENT subject — different person, angle, composition, and vibe:\n${previousSubjects.map((s, i) => `${i + 1}. ${s}`).join('\n')}`;
  }

  const prompt = `Deconstruct the following description into a professional AI image prompt framework optimized for ChatGPT Images 2.0.

KEY RULES FOR OUTPUT:
- "style" field: Be CONCRETE. Use specific references — "editorial photography, shot on Hasselblad, 90mm, f/2.8" beats "professional photo." Say "editorial" not "professional" (editorial hits a higher visual register).
- "lighting" field: Specify as a separate technical instruction — "Rembrandt key light from upper-left, soft fill from right, warm tones" not just "dramatic lighting."
- "camera" field: Include specific lens, aperture, angle. For album covers, compose for vertical layout with negative space at top/bottom for text.
- "negatives" field: Use "NO X" format — "NO watermark, NO extra limbs, NO distortion."
- "quality" field: Include resolution and rendering specs — "8k raw photo, sharp focus, photorealistic."
- "detail" field: Name specific materials and micro textures, not generic descriptions.
- If a song title or artist name is CLEARLY present in the description (labeled or unambiguously identifiable), extract into songTitle and artistName. If either is missing or ambiguous, leave that field EMPTY — do NOT invent or guess a name.
- Do NOT write out song lyrics or lyrical fragments in any field. Lyrics inform tone and imagery but must not appear as visible text in the resulting scene.
- Expand on details to make it cinematic and visually compelling.${antiCloneInstruction}

Respond with ONLY a JSON object with exactly these string fields: subject, action, environment, mood, style, lighting, camera, detail, quality, negatives, songTitle, artistName. No markdown fences, no commentary.

Description to deconstruct: "${userInput}"`;

  const response = await openaiCompatFetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
    body: JSON.stringify({
      model,
      response_format: { type: 'json_object' },
      messages: [{ role: 'user', content: prompt }],
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as OpenAIChatResponse;
    throw new Error(`OpenAI API error: ${errorData?.error?.message || `HTTP ${response.status}`}`);
  }

  try {
    const responseData = await response.json() as OpenAIChatResponse;
    const text = responseData.choices?.[0]?.message?.content;
    if (!text) throw new Error("Empty response");
    const parsed = JSON.parse(text);
    // Preserve defaults for new fields not returned by the model
    return {
      ...parsed,
      aspectRatio: parsed.aspectRatio || '1:1',
      textMedium: parsed.textMedium || '',
    };
  } catch (e) {
    throw new Error("Failed to parse AI response into framework parts.");
  }
};
